import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Clock, Filter, MessageSquare, X, XCircle } from 'lucide-react';
import { useState } from 'react';
import { appealsApi } from '../lib/api-client';
import { handleApiError } from '../lib/api-client';
import { cn } from '../lib/utils';
import type { AppealStatus, AssessmentAppeal } from '../types/api';

const STATUS_OPTIONS: { value: AppealStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ACCEPTED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

function StatusBadge({ status }: { status: AppealStatus }) {
  const map: Record<AppealStatus, { label: string; bg: string; color: string }> = {
    PENDING:  { label: 'Pending',  bg: 'rgba(245,158,11,0.1)',  color: '#b45309' },
    ACCEPTED: { label: 'Approved', bg: 'rgba(34,197,94,0.1)',   color: '#15803d' },
    REJECTED: { label: 'Rejected', bg: 'rgba(239,68,68,0.1)',   color: '#b91c1c' },
  };
  const s = map[status];
  return (
    <span
      className='inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold'
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
}

interface DecisionDialogProps {
  appeal: AssessmentAppeal;
  mode: 'approve' | 'reject';
  onClose: () => void;
  onDone: () => void;
}

function DecisionDialog({ appeal, mode, onClose, onDone }: DecisionDialogProps) {
  const [note, setNote] = useState('');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      mode === 'approve'
        ? appealsApi.approve(appeal.id, note || undefined)
        : appealsApi.reject(appeal.id, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appeals'] });
      onDone();
    },
    onError: handleApiError,
  });

  const isApprove = mode === 'approve';
  const canSubmit = isApprove ? true : note.trim().length > 0;

  return (
    <div className='fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4' onClick={onClose}>
      <div
        className='bg-white rounded-2xl w-full max-w-md shadow-xl'
        style={{ border: '1px solid var(--nhip-border)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className='flex items-center justify-between px-6 pt-5 pb-4' style={{ borderBottom: '1px solid var(--nhip-border)' }}>
          <div className='flex items-center gap-2'>
            {isApprove
              ? <CheckCircle2 className='h-5 w-5 text-green-600' />
              : <XCircle className='h-5 w-5 text-red-500' />}
            <h2 className='text-base font-semibold' style={{ color: 'var(--nhip-ink)' }}>
              {isApprove ? 'Approve Appeal' : 'Reject Appeal'}
            </h2>
          </div>
          <button onClick={onClose} className='text-gray-400 hover:text-gray-600'>
            <X className='h-4 w-4' />
          </button>
        </div>

        <div className='px-6 py-5 space-y-4'>
          {/* Appeal summary */}
          <div className='rounded-xl p-3 text-sm' style={{ backgroundColor: 'var(--nhip-surface)', border: '1px solid var(--nhip-border)' }}>
            <p className='font-medium mb-1' style={{ color: 'var(--nhip-ink)' }}>
              {appeal.facilityName ?? 'Unknown facility'}
            </p>
            <p className='text-xs line-clamp-3' style={{ color: 'var(--nhip-ink-muted)' }}>{appeal.reason}</p>
          </div>

          {isApprove ? (
            <>
              <p className='text-sm' style={{ color: 'var(--nhip-ink-muted)' }}>
                Approving will <strong>void the original assessment</strong> and notify the facility by email. A revisit can then be scheduled.
              </p>
              <div>
                <label className='block text-xs font-medium mb-1' style={{ color: 'var(--nhip-ink)' }}>
                  Reviewer note <span className='font-normal' style={{ color: 'var(--nhip-ink-muted)' }}>(optional)</span>
                </label>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  rows={3}
                  placeholder='Add a note for the facility...'
                  className='w-full px-3 py-2 text-sm rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-primary'
                  style={{ border: '1px solid var(--nhip-border)' }}
                />
              </div>
            </>
          ) : (
            <div>
              <label className='block text-xs font-medium mb-1' style={{ color: 'var(--nhip-ink)' }}>
                Rejection reason <span className='text-red-500'>*</span>
              </label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={3}
                placeholder='Explain why this appeal is being rejected...'
                className='w-full px-3 py-2 text-sm rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-primary'
                style={{ border: '1px solid var(--nhip-border)' }}
              />
            </div>
          )}
        </div>

        <div className='flex items-center justify-end gap-2 px-6 pb-5'>
          <button
            onClick={onClose}
            className='px-4 py-2 text-sm rounded-lg transition-colors hover:bg-gray-50'
            style={{ border: '1px solid var(--nhip-border)', color: 'var(--nhip-ink-muted)' }}
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors disabled:opacity-50',
              isApprove ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'
            )}
          >
            {mutation.isPending ? 'Saving…' : isApprove ? 'Approve & Void' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Appeals() {
  const [statusFilter, setStatusFilter] = useState<AppealStatus | ''>('PENDING');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<{ appeal: AssessmentAppeal; mode: 'approve' | 'reject' } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['appeals', statusFilter, page],
    queryFn: () => appealsApi.getAll({ status: statusFilter || undefined, page, limit: 20 }),
  });

  const appeals = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  return (
    <div className='p-4 sm:p-6 space-y-4 max-w-[1600px] mx-auto'>
      {/* Header */}
      <div className='flex items-center justify-between gap-4'>
        <div>
          <h1 className='text-xl font-bold' style={{ color: 'var(--nhip-ink)' }}>Appeals</h1>
          <p className='text-sm mt-0.5' style={{ color: 'var(--nhip-ink-muted)' }}>
            Review and resolve facility assessment appeals
          </p>
        </div>
        {total > 0 && (
          <span className='text-sm font-medium px-2.5 py-1 rounded-md'
            style={{ backgroundColor: 'rgba(245,158,11,0.1)', color: '#b45309' }}>
            {total} {statusFilter === 'PENDING' ? 'pending' : 'total'}
          </span>
        )}
      </div>

      {/* Status filter */}
      <div className='flex items-center gap-1.5'>
        <Filter className='h-3.5 w-3.5 flex-shrink-0' style={{ color: 'var(--nhip-ink-muted)' }} />
        {STATUS_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => { setStatusFilter(opt.value); setPage(1); }}
            className='px-3 py-1 text-xs font-medium rounded-md transition-all'
            style={
              statusFilter === opt.value
                ? { backgroundColor: 'var(--nhip-ink)', color: '#fff' }
                : { border: '1px solid var(--nhip-border)', color: 'var(--nhip-ink-muted)' }
            }
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className='bg-white rounded-2xl overflow-hidden' style={{ border: '1px solid var(--nhip-border)' }}>
        {isLoading ? (
          <div className='flex justify-center py-16'>
            <div className='h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-gray-500' />
          </div>
        ) : appeals.length === 0 ? (
          <div className='flex flex-col items-center py-16 gap-3'>
            <MessageSquare className='h-8 w-8' style={{ color: 'var(--nhip-ink-muted)' }} />
            <p className='text-sm' style={{ color: 'var(--nhip-ink-muted)' }}>No appeals found</p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--nhip-border)' }}>
                  {['Facility', 'Email', 'Reason', 'Status', 'Resolved', 'Submitted', 'Actions'].map(h => (
                    <th
                      key={h}
                      className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap'
                      style={{ color: 'var(--nhip-ink-muted)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {appeals.map(appeal => (
                  <tr key={appeal.id} className='hover:bg-gray-50 transition-colors' style={{ borderBottom: '1px solid var(--nhip-border)' }}>
                    <td className='px-4 py-2 whitespace-nowrap'>
                      <p className='font-medium' style={{ color: 'var(--nhip-ink)' }}>
                        {appeal.facilityName ?? '—'}
                      </p>
                    </td>
                    <td className='px-4 py-2 whitespace-nowrap'>
                      <p className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>{appeal.facilityEmail ?? '—'}</p>
                    </td>
                    <td className='px-4 py-2 max-w-xs'>
                      <p className='text-sm line-clamp-1' style={{ color: 'var(--nhip-ink)' }} title={appeal.reason}>{appeal.reason}</p>
                    </td>
                    <td className='px-4 py-2 whitespace-nowrap'>
                      <StatusBadge status={appeal.status} />
                    </td>
                    <td className='px-4 py-2 whitespace-nowrap'>
                      <p className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>
                        {appeal.resolvedAt ? new Date(appeal.resolvedAt).toLocaleDateString() : '—'}
                      </p>
                    </td>
                    <td className='px-4 py-2 whitespace-nowrap'>
                      <div className='flex items-center gap-1.5 text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>
                        <Clock className='h-3 w-3' />
                        {new Date(appeal.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </td>
                    <td className='px-4 py-2'>
                      {appeal.status === 'PENDING' ? (
                        <div className='flex items-center gap-2'>
                          <button
                            onClick={() => setDialog({ appeal, mode: 'approve' })}
                            className='flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors'
                          >
                            <CheckCircle2 className='h-3 w-3' />
                            Approve
                          </button>
                          <button
                            onClick={() => setDialog({ appeal, mode: 'reject' })}
                            className='flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors'
                            style={{ border: '1px solid var(--nhip-border)', color: 'var(--nhip-ink-muted)' }}
                          >
                            <XCircle className='h-3 w-3' />
                            Reject
                          </button>
                        </div>
                      ) : (
                        <div className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>
                          {appeal.status === 'ACCEPTED'
                            ? appeal.reviewerNote ?? 'Approved'
                            : appeal.rejectionReason ?? 'Rejected'}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='flex items-center justify-between px-4 py-3' style={{ borderTop: '1px solid var(--nhip-border)' }}>
            <span className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>
              Page {page} of {totalPages} · {total} total
            </span>
            <div className='flex items-center gap-1'>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className='p-1.5 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors'
                style={{ border: '1px solid var(--nhip-border)' }}
              >
                <ChevronLeft className='h-3.5 w-3.5' />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className='p-1.5 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors'
                style={{ border: '1px solid var(--nhip-border)' }}
              >
                <ChevronRight className='h-3.5 w-3.5' />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Decision dialog */}
      {dialog && (
        <DecisionDialog
          appeal={dialog.appeal}
          mode={dialog.mode}
          onClose={() => setDialog(null)}
          onDone={() => setDialog(null)}
        />
      )}
    </div>
  );
}
